import {Injectable} from '@angular/core';
import {AsaxElasticService, buildQueryTermEqual} from 'asax-elastic';
import {Observable, of, Subject} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';

export interface SkyObject {
  readonly id?: string;
  readonly objectId?: string;
  readonly resolverType?: 'SIMBAD' | 'NED';
  readonly mainId?: string;
  readonly ra?: number;
  readonly dec?: number;
  readonly longtype?: string;
  readonly radvel?: number;
  readonly redshift?: number;
  readonly isBestMatch?: boolean;
  readonly bestMatchDatasetIds?: string[];
  readonly datasetIds?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AladinSkyObjectsService {
  private skyObjectsCache = new Map<string, SkyObject>();
  private skyObjectHovered = new Subject<SkyObject>();

  constructor(private readonly asaxElasticService: AsaxElasticService) {
  }

  getSkyObjectHovered(): Observable<SkyObject> {
    return this.skyObjectHovered.asObservable();
  }

  setSkyObjectHovered(skyObject: SkyObject): void {
    this.skyObjectHovered.next(skyObject);
  }

  fetchSkyObjects(datasetIds: string[]): Observable<SkyObject> {
    const distinctDatasetIds = Array.from(new Set(datasetIds));
    const queryForObjectTypes = this.buildQueryForObjectTypes(distinctDatasetIds);
    return this.asaxElasticService.searchByQuery({
      index: 'sky-objects',
      body: {
        _source: ['id', 'ra', 'dec', 'bestMatchDatasetIds'],
        size: 1000,
        query: queryForObjectTypes
      }
    }).pipe(
      map(response => this.mapToSkyObjects(response, datasetIds)),
      catchError(() => of([]))
    );
  }

  fetchSkyObject(id: string): Observable<any> {
    const existingSkyObject = this.skyObjectsCache.get(id);

    if (existingSkyObject) {
      return of({...existingSkyObject});
    }

    const query = buildQueryTermEqual('_id', id);

    return this.asaxElasticService.searchByQuery({
      index: 'sky-objects', body: {from: 0, size: 1, query}
    }).pipe(
      map((data: any) => mapToSkyObject(data)),
      tap((skyObject) => this.skyObjectsCache.set(skyObject.id, skyObject)),
      catchError(() => of([]))
    );
  }

  private buildQueryForObjectTypes(datasetIds: string[]): any {
    const query = {bool: {should: []}};
    // const objectTypesFilter = filters.find(filter => filter.id === OBJECT_TYPES);
    const objectTypesFilter = null;
    const {longtypesSimbad, longtypesNed, isBestMatchChecked} = objectTypesFilter?.parser.getDataParsed() || {};
    const isAnyLongTypeSelected = longtypesSimbad?.length > 0 || longtypesNed?.length > 0;

    if (isAnyLongTypeSelected) {
      if (longtypesSimbad?.length > 0) {
        longtypesSimbad.forEach(longtypeSimbad => {
          const filterSimbad: any = {bool: {must: [{term: {longtype: longtypeSimbad}}, {term: {resolverType: 'SIMBAD'}}]}};

          if (isBestMatchChecked) {
            filterSimbad.bool.must.push({terms: {bestMatchDatasetIds: datasetIds}});
          } else {
            filterSimbad.bool.must.push({terms: {datasetdIds: datasetIds}});
          }

          query.bool.should.push(filterSimbad);
        });
      }

      if (longtypesNed?.length > 0) {
        longtypesNed.forEach(longtypeNed => {
          const filterNed: any = {bool: {must: [{term: {longtype: longtypeNed}}, {term: {resolverType: 'NED'}}]}};

          if (isBestMatchChecked) {
            filterNed.bool.must.push({terms: {bestMatchDatasetIds: datasetIds}});
          } else {
            filterNed.bool.must.push({terms: {datasetdIds: datasetIds}});
          }

          query.bool.should.push(filterNed);
        });
      }
    } else {
      const filterSimbad: any = {bool: {must: [{term: {resolverType: 'SIMBAD'}}]}};
      const filterNed: any = {bool: {must: [{term: {resolverType: 'NED'}}]}};

      if (isBestMatchChecked) {
        filterSimbad.bool.must.push({terms: {bestMatchDatasetIds: datasetIds}});
        filterNed.bool.must.push({terms: {bestMatchDatasetIds: datasetIds}});
      } else {
        filterSimbad.bool.must.push({terms: {datasetdIds: datasetIds}});
        filterNed.bool.must.push({terms: {datasetdIds: datasetIds}});
      }

      query.bool.should.push(filterSimbad);
      query.bool.should.push(filterNed);
    }

    return query;
  }

  private mapToSkyObjects(response: any, datasetIds: string[]): any {
    if (response?.hits?.hits) {
      return response.hits
        .hits
        .map(({_source}) => _source)
        .map((objectType: any) => ({
          ...objectType,
          isBestMatch: objectType.bestMatchDatasetIds.some(bestMatchDatasetId => datasetIds.includes(bestMatchDatasetId))
        }));
    }

    return [];
  }
}

const mapToSkyObject = (response: any): SkyObject => {
  if (response?.hits?.hits) {
    return response.hits
      .hits
      .map(({_source}) => _source)[0];
  }

  return null;
};
